<template>
  <div class="item_sales__report">
    <ReportFormat :records="reports_data" @show_result="show_result" @download_excel_report="downloadItemSalesReport" :isLoading="loading">
      <!-- Form -->
      <template #report-form>
        <div class="vx-row pt-5 px-5">
          <div class="vx-col w-full lg:w-5/12 md:w-1/2">
            <!-- from date -->
            <div class="vx-row mb-2 items-center">
              <div class="vx-col w-2/5 text-right">
                <h6>From Date</h6>
              </div>
              <div class="vx-col w-3/5 relative">
                <date-picker :appendToBody="false" v-model="fromDate" placeholder="MM-DD-YYYY" format="MM-DD-YYYY" valueType="format" class="w-full" v-validate="'required'" name="Report From Date" />
              </div>
            </div>

            <!-- item name -->
            <div class="vx-row mb-2 items-center">
              <div class="vx-col w-2/5 text-right">
                <h6>Item Name</h6>
              </div>
              <div class="vx-col w-3/5">
                <select-2
                  class="w-full"
                  autocomplete
                  :value="item"
                  @input="(elem) => (item = elem.value)"
                  name="Item"
                  label="product_name"
                  val="id"
                  placeholder="All"
                  :ssr="true"
                  action="product/getProductList"
                />
              </div>
            </div>

            <!-- client -->
            <div class="vx-row mb-2 items-center">
              <div class="vx-col w-2/5 text-right">
                <h6>Client</h6>
              </div>
              <div class="vx-col w-3/5">
                <select-2
                  class="w-full"
                  autocomplete
                  :value="client"
                  @input="(elem) => (client = elem.value)"
                  placeholder="Please Select Client"
                  name="Client"
                  @scrollEnd="get_customer_list(customer_filters.page + 1)"
                  label="business_name"
                  :loading="customer_filters.loading"
                  :extra="[{ label: 'All', value: null }]"
                  :options="customer_list_options"
                />
              </div>
            </div>
          </div>

          <div class="vx-col w-full lg:w-5/12 md:w-1/2">
            <!-- to date -->
            <div class="vx-row mb-2 items-center">
              <div class="vx-col w-2/5 text-right">
                <h6>To Date</h6>
              </div>
              <div class="vx-col w-3/5 relative">
                <date-picker :appendToBody="false" v-model="toDate" placeholder="MM-DD-YYYY" format="MM-DD-YYYY" valueType="format" class="w-full" v-validate="'required'" name="Report To Date" />
              </div>
            </div>

            <!-- sales by -->
            <div class="vx-row mb-2 items-center">
              <div class="vx-col w-2/5 text-right">
                <h6>Sales By</h6>
              </div>
              <div class="vx-col w-3/5">
                <select-2
                  class="w-full"
                  autocomplete
                  :value="salesBy"
                  @input="(elem) => (salesBy = elem.value)"
                  placeholder="Please Select Sales By"
                  name="Sales By"
                  :extra="[{ label: 'All', value: null }]"
                  :options="sales_user_list_options"
                />
              </div>
            </div>

            <!-- Category Dropdown -->
            <div class="vx-row mb-2 items-center">
              <div class="vx-col w-2/5 text-right">
                <h6>Category</h6>
              </div>
              <div class="vx-col w-3/5">
                <select-2
                  autocomplete
                  multiple
                  :closeOnSelect="false"
                  class="w-full category-input"
                  :value.sync="categoryId"
                  @input="(item) => (categoryId = item && item.value)"
                  name="Category"
                  placeholder="All"
                  label="category_name"
                  val="id"
                  v-validate="'required'"
                  :params="{ is_global: user.roleId === DEMORETAILER ? null : 'global', is_status: true }"
                  action="product/getAllProductCategories"
                />
                <span class="text-danger text-sm" v-show="errors.has('Category')">{{ errors.first('Category') }}</span>
              </div>
            </div>
          </div>
        </div>
      </template>

      <template #record-table>
        <template>
          <vs-th> # </vs-th>
          <vs-th v-for="(column, i) in tableColumns" :key="`col_${i}`">
            {{ column.name }}
          </vs-th>
        </template>

        <template ref="tableBody">
          <vs-tr>
            <vs-td></vs-td>
            <vs-td v-for="(column, i) in tableColumns" :key="`search_${i}`">
              <input class="px-2 py-2" type="text" style="border: 1px solid rgba(0, 0, 0, 0.3)" v-on="listeners(column.name)" />
            </vs-td>
          </vs-tr>

          <vs-tr :data="tr" :key="i" v-for="(tr, i) in itemSalesTableRecord">
            <vs-td>{{ i + 1 }}</vs-td>
            <vs-td>{{ tr['Invoice #'] }}</vs-td>
            <vs-td>{{ tr['Sales Person'] }}</vs-td>
            <vs-td>{{ tr['Date'] }}</vs-td>
            <vs-td>{{ tr['Client Name'] }}</vs-td>
            <vs-td>{{ tr['Category'] }}</vs-td>
            <vs-td>{{ tr['Batch #'] }}</vs-td>
            <vs-td>{{ tr['Item Name'] }}</vs-td>
            <vs-td>{{ tr['Weight Type'] }}</vs-td>
            <vs-td>{{ tr['Weight Per Package'] }}</vs-td>
            <vs-td>{{ tr['Quantity'] }}</vs-td>
            <vs-td>$ {{ tr['COGS'] | currencyFilter }} </vs-td>
            <vs-td>{{ tr['Profit'] }}</vs-td>
            <vs-td>$ {{ tr['Subtotal'] | currencyFilter }}</vs-td>
            <vs-td>$ {{ tr['Discount Amount'] | currencyFilter }}</vs-td>
            <vs-td>$ {{ tr['Reward Discount'] | currencyFilter }}</vs-td>
            <vs-td>$ {{ tr['Total Tax'] | currencyFilter }}</vs-td>
            <vs-td>$ {{ tr['Total'] | currencyFilter }}</vs-td>
          </vs-tr>

          <vs-tr v-if="reports_data.length">
            <vs-td colspan="11" class="text-right font-bold"> Total: </vs-td>
            <vs-td class="font-bold">$ {{ calculateTotalCogs | currencyFilter }} </vs-td>
            <vs-td class="font-bold">{{ calculateTotalProfit }} </vs-td>
            <vs-td class="font-bold">$ {{ calculateTotalSubTotal | currencyFilter }} </vs-td>
            <vs-td class="font-bold">$ {{ calculateTotalDiscountAmount | currencyFilter }} </vs-td>
            <vs-td class="font-bold">$ {{ calculateTotalRewardDiscountAmount | currencyFilter }} </vs-td>
            <vs-td class="font-bold">$ {{ calculateTotalTaxAmount | currencyFilter }} </vs-td>
            <vs-td class="font-bold">$ {{ calculateTotalAmount | currencyFilter }} </vs-td>
          </vs-tr>
        </template>
      </template>
    </ReportFormat>
  </div>
</template>

<script>
import XLSX from 'xlsx'
import moment from 'moment'
import DatePicker from 'vue2-datepicker'
import 'vue2-datepicker/index.css'
import { dateMixins } from '@/mixins/dateMixins.js'
import ReportFormat from '@/views/reports/components/ReportFormat.vue'
import { mapState, mapMutations } from 'vuex'
import { weightMixins } from '@/utils/weightType'
import { paymentMixins } from '@/utils/paymentTypes'
import { RETAILER_SUB_USER } from '@/utils/roles'
import { POSFunctionalMixins } from '@/mixins/functionalMixins.js'
import Select2 from '@/components/custom/form-elements/Select2.vue'
import { DEMORETAILER } from '@/utils/roles.js'
import _ from 'lodash'

export default {
  name: 'ItemSalesReport',

  components: {
    DatePicker,
    ReportFormat,
    Select2
  },

  data() {
    return {
      page: 1,
      limit: 0,
      search: '',
      date: 'MM-dd-yyyy',
      fromDate: moment().format('MM-DD-YYYY'),
      toDate: moment().format('MM-DD-YYYY'),
      salesBy: null,
      client: null,
      item: null,
      loading: false,
      categoryId: [],
      filters: {},
      DEMORETAILER,

      customer_filters: {
        page: 1,
        limit: 10,
        loading: false,
        filteredCount: 0
      },

      customer_list_options: [],

      // list of table columns
      tableColumns: [
        {
          name: 'Invoice #',
          isSearchable: true
        },
        {
          name: 'Sales Person',
          isSearchable: true
        },
        {
          name: 'Date',
          isSearchable: true
        },
        {
          name: 'Client Name',
          isSearchable: true
        },
        {
          name: 'Category',
          isSearchable: true
        },
        {
          name: 'Batch #',
          isSearchable: true
        },
        {
          name: 'Item Name',
          isSearchable: true
        },
        {
          name: 'Weight Type',
          isSearchable: true
        },
        {
          name: 'Weight Per Package',
          isSearchable: true
        },
        {
          name: 'Quantity',
          isSearchable: true
        },
        {
          name: 'COGS',
          isSearchable: true
        },
        {
          name: 'Profit',
          isSearchable: true
        },
        {
          name: 'Subtotal',
          isSearchable: true
        },
        {
          name: 'Discount Amount',
          isSearchable: true
        },
        {
          name: 'Reward Discount',
          isSearchable: true
        },
        {
          name: 'Total Tax',
          isSearchable: true
        },
        {
          name: 'Total',
          isSearchable: true
        }
      ]
    }
  },
  async mounted() {
    // fetch customer list
    await this.get_customer_list()

    // fetch sales user list
    await this.getSalesUserList()
  },

  mixins: [weightMixins, dateMixins, POSFunctionalMixins, paymentMixins],

  computed: {
    // states
    ...mapState('product', ['categories']),
    ...mapState('report', ['reports_data', 'sales_user_list', 'customer_list']),
    ...mapState('auth', ['user', 'parentUser']),

    // calculate total by name
    calculate_total_by_name() {
      return (name) => {
        return this.reports_data.reduce((acc, item) => {
          return acc + item[name]
        }, 0)
      }
    },

    sales_user_list_options() {
      return this.sales_user_list.reduce((acc, item) => {
        item.label = item.username
        item.value = item.id

        acc.push(item)

        return acc
      }, [])
    },

    itemSalesTableRecord() {
      const tempArray = []

      this.reports_data.forEach((element) => {
        element.salesItems.forEach((item) => {
          const tempObj = {}

          let sales_qty = item.sales_qty
          let rewardPointsDollar = item.redeem_dollar

          let total_cost = item.tot_price

          //check and reduce qty if item had been return
          if (element.sales_return) {
            // eslint-disable-next-line no-unused-vars
            const _ = (element.sales_return.salesItemsReturn || []).forEach((return_item) => {
              if (return_item.productId === item.productId) {
                sales_qty -= return_item.return_qty
                total_cost -= return_item.tot_per_unit
              }
            })

            const returnRedeemDollar = element.sales_return.salesItemsReturn.find((x) => x.productId === item.productId)
            rewardPointsDollar -= returnRedeemDollar && item.redeem_dollar > returnRedeemDollar.return_redeem_dollar ? returnRedeemDollar.return_redeem_dollar : item.redeem_dollar
          }
          //if item's qty all returned then don't include in report
          if (!(sales_qty > 0)) {
            return
          }

          // total discount
          const item_discount = (item.tot_discount * sales_qty) / item.sales_qty

          // total excise tax, city tax, state tax, county tax
          const excise_tax = (item.excise_tax_amt * sales_qty) / item.sales_qty
          const city_tax = (item.city_tax_amt * sales_qty) / item.sales_qty
          const state_tax = (item.state_tax_amt * sales_qty) / item.sales_qty
          const county_tax = (item.county_tax_amt * sales_qty) / item.sales_qty
          // total tax
          const item_total_tax = excise_tax + city_tax + state_tax + county_tax
          // grand_total
          const item_grand_total = total_cost + item_total_tax
          // calculate cogs
          const itemCogs = item.product.purchase_price * this.mathRound(sales_qty, 3)

          // calculate profit
          const itemProfit = itemCogs > 0 ? ((this.mathRound(total_cost, 2) - itemCogs) * 100) / itemCogs : 0

          tempObj['Invoice #'] = element.sales_code
          tempObj['Sales Person'] = element.user.business_name ? element.user.business_name : element.user.username
          tempObj.Date = dateMixins.methods.formatDate(item.sales_item_date)
          tempObj['Client Name'] = element.customer.business_name
          tempObj['Omma license #'] = element.customer.license_number || ''
          tempObj['Role Type'] = element.customer.type
          tempObj.Category = item.product.category.category_name
          tempObj['Batch #'] = item.product.batch && item.product.batch.batch_number
          tempObj['Item Name'] = item.product_name
          tempObj['Weight Type'] = item.weight_type
          tempObj['Weight Per Package'] = [this.categories.FLOWER, this.categories.SHAKE].includes(item.product.categoryId)
            ? this.mathRound(sales_qty, 3)
            : this.mathRound(item.product.weight_per_package, 3)
          tempObj.Quantity = [this.categories.FLOWER, this.categories.SHAKE].includes(item.product.categoryId) ? 0 : this.mathRound(sales_qty, 3)
          tempObj[''] = null
          tempObj.COGS = itemCogs
          tempObj.Profit = `${this.mathRound(itemProfit, 2)} %`
          tempObj.Subtotal = this.mathRound(total_cost, 2)
          tempObj['Discount Amount'] = this.mathRound(Number(item_discount), 2)
          tempObj['Reward Discount'] = this.mathRound(Number(rewardPointsDollar), 2)

          tempObj['Total Tax'] = this.mathRound(item_total_tax, 2)
          tempObj.Total = this.mathRound(Number(item_grand_total), 2)

          tempArray.push(tempObj)
        })
      })

      return tempArray.filter((x) => {
        const tempSearch = Object.keys(this.filters).map((y) => {
          if (!this.filters[y]) return true

          return !this.filters[y] || x[y].toString().toLowerCase().startsWith(this.filters[y].toLowerCase())
        })

        if (tempSearch.length && tempSearch.includes(false)) {
          return false
        }

        return true
      })
    },

    // calculate total cogs
    calculateTotalCogs() {
      return this.itemSalesTableRecord.reduce((acc, item) => {
        acc += item['COGS']
        return acc
      }, 0)
    },

    // calculate total profile
    calculateTotalProfit() {
      const totalProfit = this.calculateTotalCogs > 0 ? ((this.calculateTotalSubTotal - this.calculateTotalCogs) * 100) / this.calculateTotalCogs : 0
      return `${this.mathRound(totalProfit, 2)} %`
    },

    // calculate total sub total
    calculateTotalSubTotal() {
      return this.itemSalesTableRecord.reduce((acc, item) => {
        acc += item['Subtotal']
        return acc
      }, 0)
    },

    // calculate total discount amount
    calculateTotalDiscountAmount() {
      return this.itemSalesTableRecord.reduce((acc, item) => {
        acc += item['Discount Amount']
        return acc
      }, 0)
    },

    // calculate total reward discount amount
    calculateTotalRewardDiscountAmount() {
      return this.itemSalesTableRecord.reduce((acc, item) => {
        acc += item['Reward Discount']
        return acc
      }, 0)
    },

    // calculate total tax amount
    calculateTotalTaxAmount() {
      return this.itemSalesTableRecord.reduce((acc, item) => {
        acc += item['Total Tax']
        return acc
      }, 0)
    },

    // calculate total amount
    calculateTotalAmount() {
      return this.itemSalesTableRecord.reduce((acc, item) => {
        acc += item['Total']
        return acc
      }, 0)
    },

    listeners() {
      return (columnName) => ({
        input: _.debounce((e) => {
          this.handleDynamicFilter(e.target.value, columnName)
        }, 600)
      })
    }
  },

  methods: {
    moment,

    ...mapMutations('report', { set_state: 'SET_STATE' }),
    ...mapMutations('product', { set_products_state: 'SET_STATE' }),
    ...mapMutations('patient', { set_patient_state: 'SET_STATE' }),
    ...mapMutations('client', { set_client_state: 'SET_STATE' }),

    handleDynamicFilter(value, key) {
      this.$set(this.filters, key, value)
    },

    // get customer options
    async get_customer_list(page = this.customer_filters.page) {
      if (this.customer_list_options.length && this.customer_filters.filteredCount === this.customer_list_options.length) return false

      this.customer_filters.loading = true
      this.customer_filters.page = page

      this.$store
        .dispatch(RETAILER_SUB_USER.includes(this.parentUser.roleId) ? 'patient/getPatients' : 'client/getClients', {
          page,
          limit: this.customer_filters.limit
        })
        .then((res) => {
          this.customer_filters.filteredCount = res.FilteredCount

          if (RETAILER_SUB_USER.includes(this.parentUser.roleId)) {
            this.customer_list_options.push(...res.data.map((x) => ({ ...x, business_name: `${x.first_name} ${x.last_name}` })))
          } else {
            this.customer_list_options.push(...res.data)
          }

          this.customer_filters.loading = false
        })
        .catch((err) => {
          const { message } = err
          this.customer_filters.loading = false
          this.$vs.notify({
            title: 'Error',
            text: message,
            iconPack: 'feather',
            icon: 'icon-alert-circle',
            color: 'danger'
          })
        })
    },

    // get sales user list
    async getSalesUserList() {
      this.loading = true
      this.$store
        .dispatch('report/getSalesUserList')
        .then(() => {
          this.loading = false
        })
        .catch((err) => {
          const { message } = err
          this.loading = false
          this.$vs.notify({
            title: 'Error',
            text: message,
            iconPack: 'feather',
            icon: 'icon-alert-circle',
            color: 'danger'
          })
        })
    },

    // fetch report data
    async show_result() {
      this.loading = true
      const payload = {
        page: this.page,
        limit: this.limit,
        search: this.search,
        fromDate: moment(this.fromDate).format('YYYY-MM-DD'),
        toDate: moment(this.toDate).format('YYYY-MM-DD'),
        itemId: this.item || null,
        clientPatientId: this.client || null,
        salesBy: this.salesBy || null,
        categoryIds: this.categoryId
      }
      this.$store
        .dispatch('report/getItemSalesReport', payload)
        .then(() => {
          this.loading = false
        })
        .catch((err) => {
          const { message } = err
          this.loading = false
          this.$vs.notify({
            title: 'Error',
            text: message,
            iconPack: 'feather',
            icon: 'icon-alert-circle',
            color: 'danger'
          })
        })
    },

    // generate csv
    async downloadItemSalesReport() {
      const data = []

      let total_sub_total = 0
      let total_COGS_total = 0
      let total_discount = 0
      let total_reward_discount = 0
      let total_tax = 0
      let grand_total = 0

      // make csv data
      this.reports_data.forEach((element) => {
        element.salesItems.forEach((item) => {
          const tempObj = {}
          let sales_qty = item.sales_qty
          let rewardPointsDollar = item.redeem_dollar

          let total_cost = item.tot_price

          //check and reduce qty if item had been return
          if (element.sales_return) {
            // eslint-disable-next-line no-unused-vars
            const _ = (element.sales_return.salesItemsReturn || []).forEach((return_item) => {
              if (return_item.productId === item.productId) {
                sales_qty -= return_item.return_qty
                total_cost -= return_item.tot_per_unit
              }
            })

            const returnRedeemDollar = element.sales_return.salesItemsReturn.find((x) => x.productId === item.productId)
            rewardPointsDollar -= returnRedeemDollar && item.redeem_dollar > returnRedeemDollar.return_redeem_dollar ? returnRedeemDollar.return_redeem_dollar : item.redeem_dollar
          }
          //if item's qty all returned then don't include in report
          if (!(sales_qty > 0)) {
            return
          }

          // total discount
          const item_discount = (item.tot_discount * sales_qty) / item.sales_qty

          // total excise tax, city tax, state tax, county tax
          const excise_tax = (item.excise_tax_amt * sales_qty) / item.sales_qty
          const city_tax = (item.city_tax_amt * sales_qty) / item.sales_qty
          const state_tax = (item.state_tax_amt * sales_qty) / item.sales_qty
          const county_tax = (item.county_tax_amt * sales_qty) / item.sales_qty
          // total tax
          const item_total_tax = excise_tax + city_tax + state_tax + county_tax
          // grand_total
          const item_grand_total = total_cost + item_total_tax

          // calculate cogs
          const itemCogs = item.product.purchase_price * this.mathRound(sales_qty, 3)

          // calculate profit
          const itemProfit = itemCogs > 0 ? `${((this.mathRound(total_cost, 2) - itemCogs) * 100) / itemCogs} %` : '0 %'

          tempObj['Invoice #'] = element.sales_code
          tempObj['Sales Person'] = element.user.business_name ? element.user.business_name : element.user.username
          tempObj.Date = dateMixins.methods.formatDate(item.sales_item_date)
          tempObj['Client Name'] = element.customer.business_name
          tempObj['Omma license #'] = element.customer.license_number || ''
          tempObj['Role Type'] = element.customer.type
          tempObj.Category = item.product.category.category_name
          tempObj['Batch #'] = item.product.batch && item.product.batch.batch_number
          tempObj['Item Name'] = item.product_name
          tempObj['Weight Type'] = item.weight_type
          tempObj['Weight Per Package'] = [this.categories.FLOWER, this.categories.SHAKE].includes(item.product.categoryId)
            ? this.mathRound(sales_qty, 3)
            : this.mathRound(item.product.weight_per_package, 3)
          tempObj.Quantity = [this.categories.FLOWER, this.categories.SHAKE].includes(item.product.categoryId) ? 0 : this.mathRound(sales_qty, 3)
          tempObj[''] = null
          tempObj.COGS = itemCogs
          tempObj.Profit = `${this.mathRound(itemProfit, 2)} %`
          tempObj.Subtotal = this.mathRound(total_cost, 2)
          tempObj['Discount Amount'] = this.mathRound(Number(item_discount), 2)
          tempObj['Reward Discount'] = this.mathRound(Number(rewardPointsDollar), 2)

          tempObj['Total Tax'] = this.mathRound(item_total_tax, 2)
          tempObj.Total = this.mathRound(Number(item_grand_total), 2)

          total_sub_total += total_cost
          total_COGS_total += itemCogs
          total_discount += Number(item_discount)
          total_reward_discount += Number(rewardPointsDollar)
          total_tax += item_total_tax
          grand_total += Number(item_grand_total)

          data.push(tempObj)
        })
      })

      // total details - row
      const totalObj = {}
      totalObj[''] = 'Total'
      totalObj.COGS = this.mathRound(total_COGS_total, 2)
      totalObj.Profit = totalObj.COGS > 0 ? `${((total_sub_total - totalObj.COGS) * 100) / totalObj.COGS} %` : '0 %'
      totalObj.Subtotal = this.mathRound(total_sub_total, 2)
      totalObj['Discount Amount'] = this.mathRound(total_discount, 2)
      totalObj['Reward Discount'] = this.mathRound(total_reward_discount, 2)
      totalObj['Total Tax'] = this.mathRound(total_tax, 2)
      totalObj.Total = this.mathRound(grand_total, 2)

      // csv headers
      const columnHeader = [
        'Invoice #',
        'Sales Person',
        'Date',
        'Client Name',
        'Omma license #',
        'Role Type',
        'Category',
        'Batch #',
        'Item Name',
        'Weight Type',
        'Weight Per Package',
        'Quantity',
        '',
        'COGS',
        'Profit',
        'Subtotal',
        'Discount Amount',
        'Reward Discount',
        'Total Tax',
        'Total'
      ]

      // add total details in csv data
      data.splice(0, 0, totalObj)

      // generate csv
      const wb = XLSX.utils.book_new()
      const ws = XLSX.utils.json_to_sheet(data, { header: columnHeader })

      XLSX.utils.book_append_sheet(wb, ws, 'Sheet 1')
      wb.bookType = 'csv'
      XLSX.writeFile(wb, 'Item Sales Report.csv')
    }
  },

  watch: {
    // export csv
    async reports_data() {
      if (!this.reports_data.length) {
        this.$vs.notify({
          title: 'Warning',
          text: 'No Records Found !',
          iconPack: 'feather',
          icon: 'icon-alert-triangle',
          color: 'warning'
        })
      }
    }
  },

  filters: {
    // currencyFilter rounded to 2 point
    currencyFilter(value) {
      return paymentMixins.methods.mathRound(Number(value), 2)
    }
  },

  async beforeDestroy() {
    await this.set_state({ action: 'reports_data', data: [] })
    await this.set_products_state({ action: 'products', data: [] })
    await this.set_patient_state({ action: 'patients', data: [] })
    await this.set_client_state({ action: 'clients', data: [] })
  }
}
</script>

<style>
.category-input .input-select-con input {
  padding-right: 2.7rem;
}
</style>
